import React, { useEffect, useRef, useState } from 'react';
import { StyleParamType, wixColorParam } from '@wix/tpa-settings';
import { useSettings, useStyles } from '@wix/tpa-settings/react';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { ProGallery } from 'pro-gallery';
import { GALLERY_CONSTS, migrateOptions } from 'pro-gallery-lib';

import type { IMediaItem } from 'store/media/types';

import { Dialog } from 'wui/Dialog';

import { MediaLayout } from 'Group/constants';
import {
  settings as settingsParams,
  styles as styleParams,
} from 'settings/group';

import { MEDIA_GALLERY } from '../../dataHooks';

import { thumbnailRenderer } from './Thumbnail';

import './ProGallery.global.css';

interface MediaGalleryProps {
  items: IMediaItem[];

  fetchMore?(): void;
}

export const MediaLayoutMap = {
  [MediaLayout.grid]: GALLERY_CONSTS.layoutParams_structure_galleryLayout.GRID,
  [MediaLayout.collage]:
    GALLERY_CONSTS.layoutParams_structure_galleryLayout.COLLAGE,
  [MediaLayout.masonry]:
    GALLERY_CONSTS.layoutParams_structure_galleryLayout.MASONRY,
};

export function MediaGallery(props: MediaGalleryProps) {
  const { items, fetchMore } = props;

  const containerRef = useRef<HTMLDivElement>(null);
  const [activeId, setActiveId] = useState(-1);
  const { isMobile } = useEnvironment();
  const settings = useSettings();
  const styles = useStyles();
  const [width, setWidth] = useState(980);

  const showFullscreen = !!~activeId;

  useEffect(() => {
    setWidth(containerRef.current?.clientWidth || width);
  }, [containerRef.current]);

  const container = {
    width,
    height: 100,
    avoidGallerySelfMeasure: true,
  };

  const proGalleryOptions = {
    titlePlacement:
      GALLERY_CONSTS.behaviourParams_item_overlay_hoveringBehaviour.APPEARS,
    allowTitle: true,
    allowDescription: true,
    allowDescriptionExpand: false,
    itemClick: GALLERY_CONSTS.behaviourParams_item_clickAction.MAGNIFY,

    enableInfiniteScroll: true,
    galleryLayout:
      MediaLayoutMap[settings.get(settingsParams.mediaLayout) as MediaLayout],
    gallerySizeType: GALLERY_CONSTS.layoutParams_targetItemSize_unit.SMART,
    gridStyle: isMobile ? 0 : 1,
    floatingImages: 0,
    numberOfImagesPerRow: settings.get(settingsParams.imagesPerRow),
    loadMoreAmount:
      GALLERY_CONSTS.behaviourParams_gallery_vertical_loadMore_amount.PARTIAL,

    gallerySize: isMobile ? 0 : 30,
    galleryMargin: 0,
    imageMargin: styles.get(styleParams.layoutSpacing),
    cubeType: GALLERY_CONSTS.layoutParams_crop_method.FILL,

    cubeRatio: 1,
    cubeImages: true,
    videoLoop: true,
    videoPlay: GALLERY_CONSTS.behaviourParams_item_video_playTrigger.HOVER,
    imageLoadingMode: GALLERY_CONSTS.behaviourParams_item_content_loader.COLOR,

    hoveringBehaviour:
      GALLERY_CONSTS.behaviourParams_item_overlay_hoveringBehaviour.APPEARS,
    scrollAnimation:
      GALLERY_CONSTS.behaviourParams_gallery_scrollAnimation.NO_EFFECT,
    overlayAnimation:
      GALLERY_CONSTS.behaviourParams_item_overlay_hoverAnimation.FADE_IN,
    imageHoverAnimation:
      GALLERY_CONSTS.behaviourParams_item_content_hoverAnimation.NO_EFFECT,

    bgColorExpand: {
      value: styles.get({
        type: StyleParamType.Color,
        getDefaultValue: wixColorParam('color-1'),
        key: 'color-1',
      }).value,
    },
    actionsColorExpand: {
      value: styles.get({
        type: StyleParamType.Color,
        getDefaultValue: wixColorParam('color-5'),
        key: 'color-5',
      }).value,
    },
    groupTypes: isMobile ? '0' : '3',
  };

  return (
    <div ref={containerRef}>
      <ProGallery
        items={items}
        id={MEDIA_GALLERY}
        container={container}
        options={migrateOptions(proGalleryOptions)}
        eventsListener={handleGalleryEvents}
        customComponents={{
          customHoverRenderer: thumbnailRenderer as any,
        }}
      />
      <Dialog fullscreen isOpen={showFullscreen} onClose={closeFullscreen}>
        <ProGallery
          items={items}
          activeIndex={activeId}
          container={{
            width: window.innerWidth,
            height: window.innerHeight,
          }}
          options={migrateOptions({
            galleryLayout:
              GALLERY_CONSTS.layoutParams_structure_galleryLayout.SLIDESHOW,
            slideshowInfoSize: 0,
            slideAnimation:
              GALLERY_CONSTS.behaviourParams_gallery_horizontal_slideAnimation
                .SCROLL,
            cubeType: 'fit',
            scrollSnap: true,
            showArrows: !isMobile,
            behaviourParams: {
              item: {
                video: {
                  enableControls: true,
                },
              },
            },
            hoveringBehaviour:
              GALLERY_CONSTS.behaviourParams_item_overlay_hoveringBehaviour
                .NEVER_SHOW,
          })}
        />
      </Dialog>
    </div>
  );

  function closeFullscreen() {
    setActiveId(-1);
  }

  function handleGalleryEvents(eventName: string, data: any) {
    switch (eventName) {
      case GALLERY_CONSTS.events.NEED_MORE_ITEMS:
        fetchMore?.();
        break;
      case GALLERY_CONSTS.events.ITEM_ACTION_TRIGGERED:
        setActiveId(data.idx);
        break;
      default:
    }
  }
}

MediaGallery.displayName = 'MediaGallery';
